import { Visit } from '../types/visit';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { format } from 'date-fns';
import { CalendarIcon, Clock } from 'lucide-react';
import { PatientFollowUps } from './PatientFollowUps';
import { useToast } from '../../../hooks/use-toast';

interface DiagnosisSectionProps {
  visit: Visit;
  diagnosis: string;
  medicalHistory: string;
  onDiagnosisChange: (diagnosis: string) => void;
  onMedicalHistoryChange: (history: string) => void;
  onShowFollowUpDialog: () => void;
  onShowReminderDialog: () => void;
  onCancelReminder: (reminderId: string) => void;
}

export function DiagnosisSection({
  visit,
  diagnosis,
  medicalHistory,
  onDiagnosisChange,
  onMedicalHistoryChange,
  onShowFollowUpDialog,
  onShowReminderDialog,
  onCancelReminder
}: DiagnosisSectionProps) {
  const { toast } = useToast();

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="border-b">
        <CardTitle>Diagnosis</CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-6">
          <div>
            <Label>Diagnosis</Label>
            <Textarea
              value={diagnosis}
              onChange={(e) => onDiagnosisChange(e.target.value)}
              placeholder="Enter diagnosis details..."
              className="min-h-[200px]"
            />
          </div>
          <div>
            <Label>Update Patient's Medical History</Label>
            <Textarea
              value={medicalHistory}
              onChange={(e) => onMedicalHistoryChange(e.target.value)}
              placeholder="Update patient's medical history..."
              className="min-h-[200px]"
            />
          </div>

          <div className="pt-4 border-t space-y-6">
            <div className="flex flex-col gap-6">
              {/* Follow-ups Section */}
              <Card className="border shadow-sm">
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <CardTitle>Follow-up Visits</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onShowFollowUpDialog}
                    >
                      Schedule Follow-up
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <PatientFollowUps patientId={visit.patientId} />
                </CardContent>
              </Card>

              {/* Reminders Section */}
              <Card className="border shadow-sm">
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <CardTitle>Physician Reminders</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onShowReminderDialog}
                    >
                      Add Reminder
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {visit.physicianReminders && visit.physicianReminders.length > 0 ? (
                    <div className="space-y-3">
                      {visit.physicianReminders.map((reminder) => (
                        <Card key={reminder.id} className="border shadow-sm hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-1 text-sm">
                                    <CalendarIcon className="h-4 w-4" />
                                    {format(new Date(reminder.date), 'MMM d, yyyy')}
                                  </div>
                                  {reminder.isUrgent && (
                                    <Badge variant="destructive" className="uppercase">Urgent</Badge>
                                  )}
                                </div>
                                {reminder.notes && (
                                  <p className="text-sm text-muted-foreground mt-2">
                                    {reminder.notes}
                                  </p>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => {
                                  onCancelReminder(reminder.id);
                                  toast({
                                    title: "Reminder cancelled",
                                    description: "The physician reminder has been cancelled.",
                                  });
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="border-dashed">
                      <CardContent className="p-6">
                        <div className="flex flex-col items-center justify-center text-center space-y-2">
                          <Clock className="h-8 w-8 text-muted-foreground/60" />
                          <p className="text-sm text-muted-foreground">No reminders scheduled</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
